import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SchemaGroupFilter from '@/components/SchemaGroupFilter';
import { Search, Plus, Pencil, Trash2, ChevronDown, ChevronRight, Database, Link2, ArrowRight, Sparkles, Loader2, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

function ValuesetCard({ vs, onEdit, onDelete, onNavigate, canEdit }) {
  return (
    <div className="border border-[#E5E7EB]" data-testid={`valueset-card-${vs.valueset_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB] hover:bg-[#F3F4F6] transition-colors group">
        <button onClick={onNavigate} className="flex-1 flex items-center gap-3 text-left min-w-0" data-testid={`vs-navigate-${vs.valueset_id}`}>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-[#0A0A0A] group-hover:text-[#002FA7] transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>{vs.name}</span>
            {vs.is_derived && vs.parent_name && <span className="text-[10px] text-[#002FA7] ml-1.5">derived</span>}
            {!vs.is_derived && vs.derived_count > 0 && <span className="text-[10px] text-[#9CA3AF] ml-1.5">master ({vs.derived_count})</span>}
            {vs.description && <p className="text-xs text-[#4B5563] mt-0.5 truncate">{vs.description}</p>}
          </div>
        </button>
        <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-600 font-medium shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
          {vs.code_count} codes
        </span>
        {vs.usage_count > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 bg-[#002FA7]/5 text-[#002FA7] font-medium flex items-center gap-1 shrink-0">
            <Link2 className="w-2.5 h-2.5" /> {vs.usage_count}
          </span>
        )}
        {canEdit && (
          <>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none shrink-0" onClick={e => { e.stopPropagation(); onEdit(); }} data-testid={`edit-vs-${vs.valueset_id}`}>
              <Pencil className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none text-red-500 hover:text-red-700 shrink-0" onClick={e => { e.stopPropagation(); onDelete(); }} data-testid={`delete-vs-${vs.valueset_id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}
        <ArrowRight className="w-3.5 h-3.5 text-[#9CA3AF] opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer shrink-0" onClick={onNavigate} />
      </div>
    </div>
  );
}

export default function ValuesetsPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [valuesets, setValuesets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', codesText: '' });
  const [suggesting, setSuggesting] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all | master | derived
  const [filterScope, setFilterScope] = useState(null);

  const fetchValuesets = async () => {
    try {
      const params = new URLSearchParams();
      if (filterScope?.type === 'group') params.set('group_id', filterScope.id);
      if (filterScope?.type === 'schema') params.set('schema_id', filterScope.id);
      const r = await api.get(`/valuesets?${params}`);
      setValuesets(r.data);
    }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchValuesets(); }, [filterScope]);

  const filtered = valuesets.filter(v => {
    if (search && !v.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterType === 'master' && v.is_derived) return false;
    if (filterType === 'derived' && !v.is_derived) return false;
    return true;
  });

  const codesToText = (codes) => (codes || []).map(c => {
    let line = `${c.code}\t${c.label}`;
    if (c.parent_code) line += `\t${c.parent_code}`;
    return line;
  }).join('\n');

  const textToCodes = (text) => text.split('\n').filter(l => l.trim()).map(line => {
    const parts = line.split('\t');
    return { code: parts[0]?.trim() || '', label: parts[1]?.trim() || '', parent_code: parts[2]?.trim() || null };
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', codesText: '' });
    setDialogOpen(true);
  };

  const openEdit = async (vs) => {
    // Fetch full valueset with codes
    try {
      const r = await api.get(`/valuesets/${vs.valueset_id}`);
      setEditing(r.data);
      setForm({ name: r.data.name, description: r.data.description || '', codesText: codesToText(r.data.codes) });
      setDialogOpen(true);
    } catch (err) { toast.error('Failed to load valueset'); }
  };

  const handleAISuggestValueset = async () => {
    if (!form.name.trim()) { toast.error('Enter a name first'); return; }
    setSuggesting(true);
    try {
      const r = await api.post('/valuesets/ai-suggest', { name: form.name.trim() });
      const desc = r.data.description || '';
      const codes = (r.data.codes || []).map(c => {
        let line = `${c.code}\t${c.label}`;
        if (c.parent_code) line += `\t${c.parent_code}`;
        return line;
      }).join('\n');
      setForm(p => ({ ...p, description: desc || p.description, codesText: codes || p.codesText }));
      toast.success('AI suggestions applied');
    } catch { toast.error('AI suggestion failed'); }
    finally { setSuggesting(false); }
  };

  const handleSave = async () => {
    const codes = textToCodes(form.codesText);
    try {
      if (editing) {
        await api.put(`/valuesets/${editing.valueset_id}`, { name: form.name, description: form.description, codes });
        toast.success('Valueset updated');
      } else {
        await api.post('/valuesets', { name: form.name, description: form.description, codes });
        toast.success('Valueset created');
      }
      setDialogOpen(false);
      fetchValuesets();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (vs) => {
    if (!window.confirm(`Delete valueset "${vs.name}"?`)) return;
    try { await api.delete(`/valuesets/${vs.valueset_id}`); toast.success('Deleted'); fetchValuesets(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="valuesets-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Valuesets</h1>
            <p className="text-sm text-[#4B5563] mt-1">{filtered.length} of {valuesets.length} valuesets</p>
          </div>
          {hasPermission('edit_elements') && (
            <Button onClick={openCreate} className="rounded-none bg-[#0A0A0A] text-white hover:bg-[#171717] h-8 text-xs" data-testid="create-valueset-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Valueset
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="valueset-filters">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
            <Input data-testid="valueset-search" placeholder="Search valuesets..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-sm rounded-none" />
          </div>
          <div className="flex items-center gap-1">
            {[{v:'all',l:'All'},{v:'master',l:'Master'},{v:'derived',l:'Derived'}].map(({v,l}) => (
              <button key={v} onClick={() => setFilterType(v)} className={`text-[10px] px-2 py-1 transition-colors ${filterType === v ? 'bg-[#0A0A0A] text-white' : 'bg-zinc-100 text-[#4B5563]'}`} data-testid={`filter-vs-${v}`}>{l}</button>
            ))}
          </div>
          <SchemaGroupFilter value={filterScope} onChange={setFilterScope} />
        </div>

        <div className="space-y-px" data-testid="valuesets-list">
          {filtered.map(vs => (
            <ValuesetCard
              key={vs.valueset_id}
              vs={vs}
              canEdit={hasPermission('edit_elements')}
              onNavigate={() => navigate(`/valuesets/${vs.valueset_id}`)}
              onEdit={() => openEdit(vs)}
              onDelete={() => handleDelete(vs)}
            />
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-12 text-sm text-[#9CA3AF] border border-dashed border-[#E5E7EB]">
              {search ? `No valuesets matching "${search}"` : 'No valuesets created yet.'}
            </div>
          )}
        </div>

        {/* Valueset Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-none max-w-lg" data-testid="valueset-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit Valueset' : 'New Valueset'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Name</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-none flex-1" data-testid="vs-name-input" placeholder="e.g., Histology Codes" />
                  {!editing && (
                    <Button variant="outline" size="sm" onClick={handleAISuggestValueset} disabled={suggesting || !form.name.trim()} className="rounded-none h-9 px-3 shrink-0" data-testid="vs-ai-suggest-btn">
                      {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1.5 text-xs">AI</span>
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Description</Label>
                <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="rounded-none mt-1" data-testid="vs-desc-input" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Codes (tab-separated: code, label, parent_code)</Label>
                <Textarea
                  value={form.codesText}
                  onChange={e => setForm(p => ({ ...p, codesText: e.target.value }))}
                  className="rounded-none mt-1 font-mono text-xs"
                  data-testid="vs-codes-input"
                  rows={10}
                  placeholder={"1\tPositive\n2\tNegative\n3\tBorderline\n8501\tComedocarcinoma\t8500"}
                />
                <p className="text-[10px] text-[#9CA3AF] mt-1">One code per line. Third column (parent_code) is optional for hierarchy.</p>
              </div>
              <Button onClick={handleSave} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-valueset-btn">
                {editing ? 'Update' : 'Create'} Valueset
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
